// src/services/user/UserServiceImpl.ts

import { notificationEndpoints, userEndpoints } from '@/api/endpoints';
import type { FetcherService } from '@/services/abstract/FetcherService';
import { UserService } from '@/services/abstract/UserService';
import type { User } from '@/types/UserState';
import type { NotificationsPayloadDTO, NotificationStatus, NotificationQueryParams, NotificationStatusDTO } from '@/types/NotificationDTOs';
import type { ResponseDTO } from '@/types/DTOs';
import { buildQueryString } from '@/utils/buildQuery';
import type { CreateExternalUserPayload, CreateExternalUserResponse } from '@/types/create-six.types';

/**
 * Implementation of user service for managing current user profile
 * and notifications.
 *
 * Provides functionality for:
 * - Retrieving current user information
 * - Updating user profile details
 * - Fetching user notifications
 * - Marking notifications as read
 *
 * @see UserService
 */
export class UserServiceImpl extends UserService {
  /**
   * Creates an instance of UserServiceImpl
   * @param fetcher - Service for making HTTP requests to the backend
   */
  constructor(private readonly fetcher: FetcherService) {
    super();
  }

  /**
   * Retrieves the current authenticated user's profile information
   * @endpoint GET /private/v1/user/{id}
   * @returns User object containing current user's profile data
   * @throws UnauthorizedError if user is not authenticated
   * @tags user, profile
   */
  async getMe(id: string): Promise<User> {
    return await this.fetcher.get<User>(userEndpoints.getUser(id));
  }

  async getEncapsKey(id: string): Promise<{ encapsKey: string }> {
    return await this.fetcher.get<{ encapsKey: string }>(userEndpoints.getEncapsKey(id));
  }

  async getDecapsKey(id: string): Promise<{ decapsKey: string }> {
    return await this.fetcher.get<{ decapsKey: string }>(userEndpoints.getDecapsKey(id));
  }

  /**
   * Updates the current authenticated user's profile information
   *
   * Allows partial updates - only provided fields will be modified.
   * Omitted fields will remain unchanged.
   *
   * @endpoint PATCH /private/v1/user/{id}
   * @param id - User ID
   * @param name - Optional new display name
   * @param phone - Optional new phone number
   * @param department - Optional new department
   * @returns void
   * @throws UnauthorizedError if user is not authenticated
   * @throws ValidationError if provided data is invalid
   * @tags user, profile, update
   */
  async updateMe(id: string, name?: string, phone?: string, department?: string): Promise<void> {
    await this.fetcher.patch(userEndpoints.updateUser(id), { name, phone, department });
  }

  /**
   * Retrieves the current user's notifications
   * @endpoint GET /private/v1/notifications
   * @param params - Optional query params (page, pageSize, read)
   * @returns NotificationsPayloadDTO containing notification items and unread count
   * @throws UnauthorizedError if user is not authenticated
   * @tags user, notifications
   */
  async getNotifications(params?: NotificationQueryParams): Promise<NotificationsPayloadDTO> {
    const url = `${notificationEndpoints.getNotifications}${buildQueryString(params !== undefined ? { ...params } : undefined)}`;
    return await this.fetcher.get<NotificationsPayloadDTO>(url);
  }

  /**
   * Marks a single notification as read
   * @endpoint POST /private/v1/notifications/{id}/mark-read
   * @param notificationId - ID of the notification to mark as read
   * @throws UnauthorizedError if user is not authenticated
   * @tags notifications
   */
  async markNotificationRead(notificationId: string, status: NotificationStatus ): Promise<NotificationStatusDTO> {
    return await this.fetcher.patch(notificationEndpoints.markRead(notificationId), {status});
  }

  /**
   * Marks all notifications as read
   * @endpoint POST /private/v1/notifications/mark-all-read
   * @throws UnauthorizedError if user is not authenticated
   * @tags notifications
   */
  async markAllNotificationsRead(status: NotificationStatus): Promise<ResponseDTO> {
   return await this.fetcher.patch(notificationEndpoints.markAllRead, {status});
  }

  async createNewExternalUser(payload: CreateExternalUserPayload): Promise<CreateExternalUserResponse> {
    return await this.fetcher.post<CreateExternalUserResponse>(userEndpoints.createNewExternalUser, payload);
  }
}
